import Decimal from '@st/decimal'
import {
  useVipClubStore,
  isAuthLoyaltyData,
} from '../../stores/useVipClubStore'
import type { Level } from '../../types'

type Locale = 'en' | 'ru' | 'es' | 'pt'

interface useVipClubReturn {
  currentCashbackMultiplier: Ref<string | undefined>
  nextCashbackMultiplier: Ref<string | undefined>
  nextCashbackTitle: Ref<string | undefined>
  reloadDropAmount: Ref<string>
  reloadDropAmountThreshold: Ref<string>
  reloadExpiredAt: Ref<string | undefined>
  reloadDropAmountCurrency: Ref<string>
  levels: Ref<Level[] | undefined>
  userLevel: Ref<Level | undefined>
  loyaltyCurrency: Ref<string>
  userLevelTitle: Ref<string>
  nextLevelTitle: Ref<string>
  levelIcon: Ref<string>
  nextLevelIcon: Ref<string>
  progressInCurrency: Ref<string>
  progressPercent: Ref<string>
  hasZeroBets: Ref<boolean>
  betsToJoinVipClub: Ref<string>
  backgroundColor: Ref<string>
  backgroundMobileColor: Ref<string>
  cashbackId: Ref<string | undefined>
  reloadId: Ref<string | undefined>
  loading: Ref<boolean>
}

export function useVipClub(): useVipClubReturn {
  const { cashbackData, reloadData, loyaltyData, loading } =
    storeToRefs(useVipClubStore())

  const { locale } = useNuxtApp().$i18n

  // cashback
  const cashback = computed(
    () => cashbackData.value?.find((item) => item.status === 'ACTIVE'),
  )

  const cashbackId = computed(() => cashback.value?.wayId)

  const currentCashBackLevel = computed(
    () =>
      cashback.value?.data.levels.find((level) => level.status === 'ACTIVE'),
  )

  const nextCashBackLevel = computed(() => {
    if (!cashback.value) return undefined
    const { levels } = cashback.value.data
    const currentIdx = levels.findIndex((level) => level.status === 'ACTIVE')
    return currentIdx !== -1 ? levels[currentIdx + 1] : undefined
  })

  const currentCashbackMultiplier = computed(() =>
    new Decimal(currentCashBackLevel.value?.cashbackMultiplier || '0')
      .mul(100)
      .toString(),
  )

  const nextCashbackMultiplier = computed(() =>
    new Decimal(nextCashBackLevel.value?.cashbackMultiplier || '0')
      .mul(100)
      .toString(),
  )

  const nextCashbackTitle = computed(
    () => nextCashBackLevel.value?.title[locale.value as Locale],
  )

  // reload
  const reload = computed(
    () => reloadData.value?.find((item) => item.status === 'ACTIVE'),
  )

  const reloadId = computed(() => reload.value?.wayId)

  const reloadDropAmount = computed(() => reload.value?.data.dropAmount || '0')

  const reloadDropAmountThreshold = computed(
    () => reload.value?.data.dropAmountThreshold || '0',
  )

  const reloadExpiredAt = computed(() => reload.value?.data.expiredAt || '0')

  const reloadDropAmountCurrency = computed(
    () => reload.value?.data.dropAmountCurrency ?? 'USD',
  )

  // loyalty
  const loyalty = computed(
    () => loyaltyData.value?.find((item) => item.status === 'ACTIVE'),
  )

  const levels = computed(() => loyalty.value?.data.levels)

  const userLevel = computed(
    () => levels.value?.find((item) => item.status === 'ACTIVE'),
  )
  const userNextLevel = computed(
    () => levels.value?.find((item) => item.status === 'LOCKED'),
  )

  const loyaltyCurrency = computed(
    () => loyalty.value?.data.progressCurrency || 'USD',
  )

  const { format: formatFiat } = useCurrencyFormatter({
    currency: loyaltyCurrency.value,
    roundingMode: 'trunc',
  })

  const userLevelTitle = computed(
    () =>
      levels.value?.find((item) => item.status === 'ACTIVE')?.title[
        locale.value as Locale
      ] || '',
  )

  const nextLevelTitle = computed(
    () =>
      levels.value?.find((item) => item.status === 'LOCKED')?.title[
        locale.value as Locale
      ] || '',
  )

  const levelIcon = computed(
    () =>
      levels.value?.find((item) => item.status === 'ACTIVE' && item.index !== 0)
        ?.icon || '',
  )

  const nextLevelIcon = computed(() => userNextLevel.value?.icon || '')

  const loyaltyProgressCount = computed(() => {
    if (!loyalty.value) return '0'

    return isAuthLoyaltyData(loyalty.value)
      ? loyalty.value?.data?.progressCount
      : '0'
  })

  const nextLevelProgressTarget = computed(
    () => userNextLevel.value?.progressTarget || '0',
  )

  const progressInCurrency = computed(
    () =>
      `${formatFiat(loyaltyProgressCount.value || '0')} / ${formatFiat(
        nextLevelProgressTarget.value || '0',
      )}`,
  )

  const progressPercent = computed(() => {
    const percent = new Decimal(loyaltyProgressCount.value ?? '0')
      .div(new Decimal(nextLevelProgressTarget.value))
      .mul(100)
      .toDecimalPlaces(0)
      .toNumber()
    return `${percent}%`
  })

  const hasZeroBets = computed(() => {
    if (!loyalty.value) return false
    return (
      loyaltyProgressCount.value === null ||
      new Decimal(loyaltyProgressCount.value ?? '0').equals(0)
    )
  })

  const betsToJoinVipClub = computed(() =>
    formatFiat(nextLevelProgressTarget.value),
  )

  const backgroundColor = computed(
    () => userLevel.value?.backgroundGradient || '',
  )

  const backgroundMobileColor = computed(
    () => userLevel.value?.background || 'var(--background-base)',
  )

  return {
    currentCashbackMultiplier,
    nextCashbackMultiplier,
    nextCashbackTitle,
    reloadDropAmount,
    reloadDropAmountThreshold,
    reloadExpiredAt,
    reloadDropAmountCurrency,
    levels,
    userLevel,
    loyaltyCurrency,
    userLevelTitle,
    nextLevelTitle,
    levelIcon,
    nextLevelIcon,
    progressInCurrency,
    progressPercent,
    hasZeroBets,
    betsToJoinVipClub,
    backgroundColor,
    backgroundMobileColor,
    cashbackId,
    reloadId,
    loading,
  }
}
